import React, {forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import YoutubeVideoPlayer from "./youtube";
import NativeVideoPlayer from "./native";
import './index.scss';
import type YouTubePlayer from "yt-player";
import {useUpdateEffect} from "react-use";
import {Utils} from "../../../core";

/**
 * The Types of Video player uis.
 */
export enum VideoPlayerTypes {
    small = 'small',
    large = 'large',
}

export interface VideoPlayerProps {
    muted?: boolean,
    local?: boolean,
    noControls?: boolean,
    id?: string,
    url: string | string[],
    title?: string,
    type?: VideoPlayerTypes,
    className?: string,
    autoPlay?: boolean,
}

export interface VideoPlayerRef<T = any> {

    pause(): void;

    play(): void;

    mute(): void;

    unmute(): void;

    fullscreen(): void;

    timestamp(): void;

    seek(time: number): void;

    getPlayerInstance(): [boolean, T];
}

export const initialVideoPlayerRef: VideoPlayerRef = {
    pause: () => void 0,
    play: () => void 0,
    mute: () => void 0,
    unmute: () => void 0,
    fullscreen: () => void 0,
    timestamp: () => void 0,
    seek: () => void 0,
    getPlayerInstance: () => [false, void 0],
}

const VideoPlayer = forwardRef<VideoPlayerRef, VideoPlayerProps>((props, ref) => {
    const idRef = useRef(props.id ?? Utils.createUUId());
    const innerRef = useRef<VideoPlayerRef>(initialVideoPlayerRef);
    const urls = useMemo(() => Array.isArray(props.url) ? props.url : [props.url], [props.url]);
    const correctedUrls = useMemo(() => urls.map(url => props.local ? url : Utils.correctUrl(url)), [urls, props.local]);
    const [urlIndex, setUrlIndex] = useState(0);

    const url = useMemo(() => correctedUrls[urlIndex], [correctedUrls, urlIndex]);
    const youtubeVideoId = useMemo(() => Utils.getYoutubeVideoId(url), [url]);

    const defaultProps = useMemo(() => ({
        title: props.title ?? 'Video Player',
        type: props.type ?? VideoPlayerTypes.small,
        className: props.className ?? '',
        autoPlay: props.autoPlay ?? false,
        muted: props.muted ?? false,
        noControls: props.noControls ?? false,
    }), [props.title, props.type, props.className, props.autoPlay, props.muted, props.noControls]);

    /**
     * Injects the properties obtained from the inner ref to the given ref.
     * * The properties are identical from those injected in the Youtube players.
     */
    useImperativeHandle(ref, () => ({
        ...(innerRef.current ?? {})
    }), [innerRef])

    /**
     * Changes the video player to the next video when the current video ends.
     * * This only happens when the autoplay is enabled.
     * * This only happens when the video is from Youtube.
     */
    useEffect(() => {
        if (!innerRef.current || !props.autoPlay) {
            return
        }
        const [isYoutube, instance] = innerRef.current.getPlayerInstance();
        const listener = () => {
            setUrlIndex((prev) => {
                return prev + 1 > correctedUrls.length - 1 ? 0 : prev + 1
            });
        }
        if (isYoutube) {
            const typedInstance = instance as YouTubePlayer;
            typedInstance.on('ended', listener)
            return () => {
                typedInstance.off('ended', listener);
            }
        } else {
            const typedInstance = instance as HTMLVideoElement;
            typedInstance.addEventListener('ended', listener)
            return () => {
                typedInstance.removeEventListener('ended', listener);
            }
        }
    }, [correctedUrls.length, props.autoPlay, urlIndex]);

    useUpdateEffect(() => {
        setTimeout(() => {
            const [isYoutube, instance] = innerRef.current.getPlayerInstance();
            if (!isYoutube && props.autoPlay) {
                const typedInstance = instance as HTMLVideoElement;
                typedInstance?.load();
                typedInstance.playsInline = true;
                typedInstance.muted = true;
                typedInstance?.play().then(() => {
                    if (typedInstance) {
                        typedInstance.playsInline = true;
                        typedInstance.muted = true;
                    }
                });
            }
        }, 300)
    }, [props.autoPlay, urlIndex]);

    return (
        <>
            {
                !!youtubeVideoId
                    ? <YoutubeVideoPlayer
                        key={idRef.current}
                        ref={innerRef}
                        id={`video-${idRef.current}`}
                        videoId={youtubeVideoId}
                        {...defaultProps}
                    />
                    : <NativeVideoPlayer
                        key={idRef.current}
                        ref={innerRef}
                        id={`video-${idRef.current}`}
                        src={url}
                        {...defaultProps}
                    />
            }
        </>
    )
});

export default VideoPlayer;
